/**
 * Firestore 기반 실시간 게임 동기화 서비스
 * 30명 이상의 다중 사용자 환경을 위한 확장 가능한 아키텍처
 */

import { 
  doc, 
  onSnapshot, 
  updateDoc, 
  setDoc,
  getDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  increment,
  runTransaction
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Room, Player, Question, GameState, GameSettings } from '../types/game';
import logger from '../utils/logger';

export interface RealtimeGameData {
  room: Room | null;
  players: { [playerId: string]: Player }; // 객체로 변경하여 O(1) 접근
  questions: Question[];
  gameState: GameState;
  currentQuestionIndex: number;
  gameSettings: GameSettings;
  hasStarted: boolean;
  phaseStartedAt: number | null; // ms 단위 타임스탬프
  phaseDuration: number | null; // 초 단위 (Firestore 저장 시에도 초 단위로 저장)
  paused: boolean;
  pausedPrevState?: GameState | null;
  lastGradedQuestionId?: string | null;
  activeQuestionId?: string | null;
  lastUpdated: number;
  version: number; // 동시성 제어를 위한 버전
}

// GameUpdate 인터페이스는 현재 사용하지 않으므로 제거

class RealtimeGameSync {
  private roomId: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private pendingUpdates: Map<string, Promise<void>> = new Map(); // 중복 업데이트 방지
  private reconnectTimeout: NodeJS.Timeout | null = null; // 재연결 타이머
  private reconnectAttempts: number = 0; // 재연결 시도 횟수
  private maxReconnectAttempts: number = 5; // 최대 재연결 시도 횟수

  /**
   * 방에 연결하고 실시간 동기화 시작
   */
  async connectToRoom(roomCode: string): Promise<void> {
    
    if (this.roomId === roomCode && this.unsubscribe) {
      
      return; // 이미 연결됨
    }

    // 기존 연결 해제
    this.disconnect();

    this.roomId = roomCode;
    const roomRef = doc(db, 'game_rooms', roomCode);
    

    // 실시간 리스너 설정
    this.unsubscribe = onSnapshot(roomRef, (docSnapshot) => {
      
      if (docSnapshot.exists()) {
        const data = docSnapshot.data() as RealtimeGameData;
        
        this.handleRealtimeUpdate(data);
      } else {
        
        this.notifyListeners('ROOM_NOT_FOUND', null);
      }
    }, (error) => {
      logger.error('🔥 Firestore 실시간 동기화 오류:', error);
      this.notifyListeners('SYNC_ERROR', error);
      
      if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
        this.handleReconnect(roomCode);
      }
    });

    
  }

  /**
   * 재연결 처리
   */
  private handleReconnect(roomCode: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('🔥 최대 재연결 시도 횟수 초과, 재연결 중단');
      this.notifyListeners('RECONNECT_FAILED', { attempts: this.reconnectAttempts });
      return;
    }

    // 기존 재연결 타이머 정리
    if (this.reconnectTimeout) {
      try {
        clearTimeout(this.reconnectTimeout);
      } catch (error) {
        console.warn('재연결 타이머 정리 중 오류:', error);
      }
      this.reconnectTimeout = null;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000); // 지수 백오프, 최대 30초
    
    logger.warn(`🔥 네트워크 오류 감지, 재연결 시도 중... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.connectToRoom(roomCode).catch(error => {
        logger.error('🔥 재연결 실패:', error);
        this.handleReconnect(roomCode);
      });
    }, delay);
  }

  /**
   * 방 생성 (호스트용)
   */
  async createRoom(roomData: Partial<RealtimeGameData>): Promise<void> {
    if (!this.roomId) throw new Error('Room ID가 설정되지 않음');

    // room 객체에서 hostId 추출
    const hostId = roomData.room?.hostId || null;
    
    const initialData: RealtimeGameData = {
      room: roomData.room || null,
      players: {},
      questions: roomData.questions || [],
      gameState: 'waiting',
      currentQuestionIndex: 0,
      gameSettings: roomData.gameSettings || {
        timeLimit: 30,
        answerRevealTime: 10,
        eliminationMode: false,
        eliminationThreshold: 3,
        autoMode: true,
        showCorrectAnswer: true,
        allowMultipleAttempts: false,
        randomizeQuestions: false,
        randomizeOptions: false
      },
      hasStarted: false,
      phaseStartedAt: null,
      phaseDuration: null,
      paused: false,
      lastUpdated: Date.now(),
      version: 1
    };

    const roomRef = doc(db, 'game_rooms', this.roomId);
    
    // hostId를 최상위 레벨에도 저장 (쿼리 가능하도록)
    const docData: any = {
      ...initialData,
      hostId: hostId, // 최상위 레벨에 hostId 저장 (인덱싱 및 쿼리용)
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    try {
      await setDoc(roomRef, docData);
      
      const { getDoc } = await import('firebase/firestore');
      const immediateCheck = await getDoc(roomRef);
      if (!immediateCheck.exists()) {
        logger.error('🔥 setDoc 직후 즉시 확인: 없음!');
      }
    } catch (error) {
      logger.error('🔥 Firestore setDoc 실패:', error);
      throw error;
    }
    
    try {
      const { getDoc } = await import('firebase/firestore');
      const savedDoc = await getDoc(roomRef);
      if (!savedDoc.exists()) {
        logger.error('🔥 방 저장 실패: 문서가 존재하지 않음');
      }
    } catch (checkError) {
      logger.error('🔥 방 존재 확인 실패:', checkError);
    }
    
  }

  /**
   * 플레이어 참여 (원자적 연산)
   */
  async joinPlayer(player: Player): Promise<void> {
    if (!this.roomId) throw new Error('Room ID가 설정되지 않음');

    const roomRef = doc(db, 'game_rooms', this.roomId);
    
    await runTransaction(db, async (transaction) => {
      const roomDoc = await transaction.get(roomRef);
      
      if (!roomDoc.exists()) {
        throw new Error('방을 찾을 수 없습니다');
      }

      const data = roomDoc.data() as RealtimeGameData;
      
      // 중복 참여 방지
      if (data.players[player.id]) {
        return; // 이미 참여함
      }

      // 닉네임 중복 검사
      const existingPlayers = Object.values(data.players);
      const isNicknameDuplicate = existingPlayers.some(p => 
        p.nickname && p.nickname.toLowerCase() === player.nickname.toLowerCase()
      );
      
      if (isNicknameDuplicate) {
        throw new Error('이미 사용 중인 닉네임입니다. 다른 닉네임을 선택해주세요.');
      }

      // 플레이어 추가 (undefined 값 제거)
      const cleanPlayer = Object.fromEntries(
        Object.entries(player).filter(([_, v]) => v !== undefined)
      );
      
      transaction.update(roomRef, {
        [`players.${player.id}`]: cleanPlayer,
        lastUpdated: Date.now(),
        version: increment(1),
        updatedAt: serverTimestamp()
      });
    });

    logger.debug('🔥 플레이어 참여 완료:', player.nickname);
  }

  /**
   * 플레이어 나가기 (원자적 연산)
   */
  async leavePlayer(playerId: string): Promise<void> {
    if (!this.roomId) throw new Error('Room ID가 설정되지 않음');

    const roomRef = doc(db, 'game_rooms', this.roomId);
    
    await runTransaction(db, async (transaction) => {
      const roomDoc = await transaction.get(roomRef);
      
      if (!roomDoc.exists()) {
        return; // 방이 없으면 무시
      }

      const data = roomDoc.data() as RealtimeGameData;
      
      if (!data.players[playerId]) {
        return; // 플레이어가 없으면 무시
      }

      // 플레이어 제거
      const updatedPlayers = { ...data.players };
      delete updatedPlayers[playerId];

      // 방 자동 삭제 비활성화 (호스트가 혼자 있을 수 있음)
      if (false) { // Object.keys(updatedPlayers).length === 0) {
        transaction.delete(roomRef);
        
      } else {
        transaction.update(roomRef, {
          players: updatedPlayers,
          lastUpdated: Date.now(),
          version: increment(1),
          updatedAt: serverTimestamp(),
        });
      }
    });

    logger.debug('🔥 플레이어 나가기 완료:', playerId);
  }

  /**
   * 플레이어 강퇴 (원자적 연산)
   */
  async kickPlayer(playerId: string): Promise<void> {
    if (!this.roomId) throw new Error('Room ID가 설정되지 않음');

    const roomRef = doc(db, 'game_rooms', this.roomId);
    
    try {
      await runTransaction(db, async (transaction) => {
        const roomDoc = await transaction.get(roomRef);
        
        if (!roomDoc.exists()) {
          throw new Error('방을 찾을 수 없습니다');
        }

        const data = roomDoc.data() as RealtimeGameData;
        
        // 플레이어가 이미 제거된 경우 성공으로 간주 (중복 강퇴 방지)
        if (!data.players[playerId]) {
          logger.debug('플레이어가 이미 제거됨:', playerId);
          return; // 트랜잭션을 중단하지만 성공으로 간주
        }

        // 플레이어 제거
        const updatedPlayers = { ...data.players };
        delete updatedPlayers[playerId];

        // ⚠️ 강퇴 후에도 방을 유지하여 재참여 가능하도록 항상 업데이트만 수행
        transaction.update(roomRef, {
          players: updatedPlayers,
          lastUpdated: Date.now(),
          version: increment(1),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error: any) {
      // failed-precondition 오류는 문서가 이미 업데이트된 경우 발생할 수 있음
      // 플레이어가 이미 제거되었는지 확인
      if (error?.code === 'failed-precondition') {
        try {
          const roomDoc = await getDoc(roomRef);
          if (roomDoc.exists()) {
            const data = roomDoc.data() as RealtimeGameData;
            // 플레이어가 이미 제거되었다면 성공으로 간주
            if (!data.players[playerId]) {
              logger.debug('플레이어가 이미 제거됨 (트랜잭션 충돌 후 확인):', playerId);
              // 강퇴 이벤트는 브로드캐스트 (이미 제거되었어도 이벤트는 전송)
              this.notifyListeners('PLAYER_KICKED', { playerId, roomId: this.roomId });
              return;
            }
          }
        } catch (checkError) {
          // 확인 실패 시 원래 오류를 다시 throw
          logger.warn('강퇴 후 상태 확인 실패:', checkError);
        }
      }
      // 다른 오류이거나 플레이어가 아직 존재하는 경우 오류를 다시 throw
      throw error;
    }

    // 강퇴 이벤트 브로드캐스트 (강퇴당한 플레이어가 즉시 감지할 수 있도록)
    this.notifyListeners('PLAYER_KICKED', { playerId, roomId: this.roomId });

    logger.debug('🔥 플레이어 강퇴 완료:', playerId);
  }

  /**
   * 플레이어 상태 업데이트 (점수, 답안 등)
   */
  async updatePlayer(playerId: string, updates: Partial<Player>): Promise<void> {
    if (!this.roomId) throw new Error('Room ID가 설정되지 않음');

    const roomRef = doc(db, 'game_rooms', this.roomId);
    const updateData: any = {};
    
    // 각 필드를 개별적으로 업데이트 (undefined/null 값 제외)
    Object.keys(updates).forEach(key => {
      const value = updates[key as keyof Player];
      if (value !== undefined && value !== null) {
        updateData[`players.${playerId}.${key}`] = value;
      }
    });

    updateData.lastUpdated = Date.now();
    updateData.version = increment(1);
    updateData.updatedAt = serverTimestamp();

    await updateDoc(roomRef, updateData);
    
  }

  /**
   * 게임 상태 업데이트 (원자적 연산, 재시도 로직 포함)
   * 중복 방지 로직 개선: 플레이어 상태는 항상 동기화 보장
   */
  async updateGameState(updates: Partial<RealtimeGameData>, retryCount = 0): Promise<void> {
    
    if (!this.roomId) {
      logger.error('[RT] Room ID가 설정되지 않음');
      throw new Error('Room ID가 설정되지 않음');
    }

    const updateKey = Object.keys(updates).sort().join(',');
    
    // 중요 업데이트: 게임 상태, 플레이어, 문제 인덱스, 채점 상태 변경
    // 중요 업데이트는 항상 실행되며 버전 증가, 중복 방지 없음
    const isCriticalUpdate = updates.players !== undefined || 
                            updates.gameState !== undefined || 
                            updates.currentQuestionIndex !== undefined ||
                            updates.lastGradedQuestionId !== undefined;
    
    if (!isCriticalUpdate && this.pendingUpdates.has(updateKey)) {
      try {
        await this.pendingUpdates.get(updateKey);
        return;
      } catch (error) {
        this.pendingUpdates.delete(updateKey);
      }
    }

    const updatePromise = this._executeUpdate(updates, retryCount);
    
    if (!isCriticalUpdate) {
      this.pendingUpdates.set(updateKey, updatePromise);
    }
    
    try {
      await updatePromise;
    } catch (error) {
      throw error;
    } finally {
      if (!isCriticalUpdate) {
        this.pendingUpdates.delete(updateKey);
      }
    }
  }

  /**
   * 재귀적으로 undefined 값을 제거하는 헬퍼 함수
   * Firestore는 undefined 값을 허용하지 않으므로 모든 중첩 객체에서 제거 필요
   */
  private removeUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }
    
    if (typeof obj === 'object' && obj.constructor === Object) {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = this.removeUndefinedValues(value);
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  /**
   * 실제 업데이트 실행 (내부 메서드)
   * 재시도 로직 강화 및 배치 업데이트 최적화
   */
  private async _executeUpdate(updates: Partial<RealtimeGameData>, attemptCount = 0): Promise<void> {
    const roomRef = doc(db, 'game_rooms', this.roomId!);
    
    // 최상위 레벨에서 undefined 제거
    const cleanUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, v]) => v !== undefined)
    );
    
    // 재귀적으로 모든 중첩 객체에서 undefined 제거
    const fullyCleanedUpdates = this.removeUndefinedValues(cleanUpdates);
    
    const maxRetries = updates.players !== undefined ? 5 : 3;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const updateData: any = {
          ...fullyCleanedUpdates,
          lastUpdated: Date.now(),
          updatedAt: serverTimestamp()
        };
        
        // 중요 업데이트만 버전 증가 (동시성 제어를 위해)
        // 중요 업데이트: players, gameState, currentQuestionIndex, lastGradedQuestionId
        const isCriticalUpdate = updates.players !== undefined || 
                                updates.gameState !== undefined || 
                                updates.currentQuestionIndex !== undefined ||
                                updates.lastGradedQuestionId !== undefined;
        if (isCriticalUpdate) {
          updateData.version = increment(1);
        }
        
        await updateDoc(roomRef, updateData);
        return;
      } catch (error: any) {
        console.error(`[RT] 게임 상태 업데이트 실패 (시도 ${attempt + 1}/${maxRetries}):`, error.message);
        
        if (attempt < maxRetries - 1) {
          const delay = updates.players !== undefined 
            ? 50 * (attempt + 1)
            : 100 * (attempt + 1);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        console.error('[RT] 최대 재시도 횟수 초과, 에러 throw');
        throw error;
      }
    }
  }

  /**
   * 호스트 활동 핑 (버전 증가 없이 갱신 시간만 갱신)
   */
  async ping(): Promise<void> {
    if (!this.roomId) throw new Error('Room ID가 설정되지 않음');
    const roomRef = doc(db, 'game_rooms', this.roomId);
    await updateDoc(roomRef, {
      lastUpdated: Date.now(),
      updatedAt: serverTimestamp()
    } as any);
  }

  /**
   * 배치 업데이트 (여러 플레이어 동시 업데이트)
   */
  async batchUpdatePlayers(playerUpdates: { [playerId: string]: Partial<Player> }): Promise<void> {
    if (!this.roomId) throw new Error('Room ID가 설정되지 않음');

    const batch = writeBatch(db);
    const roomRef = doc(db, 'game_rooms', this.roomId);

    const updateData: any = {
      lastUpdated: Date.now(),
      version: increment(1),
      updatedAt: serverTimestamp()
    };

    // 각 플레이어 업데이트를 배치에 추가 (undefined 값 제외)
    Object.entries(playerUpdates).forEach(([playerId, updates]) => {
      Object.keys(updates).forEach(key => {
        const value = updates[key as keyof Player];
        if (value !== undefined) {
          updateData[`players.${playerId}.${key}`] = value;
        }
      });
    });

    batch.update(roomRef, updateData);
    await batch.commit();

    logger.debug('🔥 배치 플레이어 업데이트 완료:', Object.keys(playerUpdates));
  }

  /**
   * 실시간 업데이트 처리
   */
  private handleRealtimeUpdate(data: RealtimeGameData): void {
    // 데이터를 그대로 전달 (FirestoreSyncManager에서 배열 변환 처리)
    this.notifyListeners('GAME_DATA_UPDATE', data);
  }

  /**
   * 방 삭제
   */
  async deleteRoom(): Promise<void> {
    if (!this.roomId) return;

    const roomRef = doc(db, 'game_rooms', this.roomId);
    
    try {
      await deleteDoc(roomRef);
      
      this.notifyListeners('ROOM_DELETED', { roomId: this.roomId });
    } catch (error) {
      logger.error('🔥 방 삭제 실패:', error);
      throw error;
    } finally {
      this.disconnect();
    }
  }

  /**
   * 연결 해제
   */
  disconnect(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    this.roomId = null;
    this.pendingUpdates.clear();
    this.reconnectAttempts = 0; // 재연결 시도 횟수 초기화
    
  }

  /**
   * 이벤트 리스너 등록
   */
  addEventListener(eventType: string, callback: Function): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);
    
  }

  /**
   * 이벤트 리스너 제거
   */
  removeEventListener(eventType: string, callback: Function): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * 리스너들에게 알림
   */
  private notifyListeners(eventType: string, data: any): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          logger.error('🔥 리스너 콜백 오류:', error);
        }
      });
    } else {
      
    }
  }

  /**
   * 현재 방 상태 가져오기 (일회성)
   */
  async getCurrentRoomData(): Promise<RealtimeGameData | null> {
    if (!this.roomId) return null;

    const roomRef = doc(db, 'game_rooms', this.roomId);
    const docSnapshot = await getDoc(roomRef);
    
    if (docSnapshot.exists()) {
      return docSnapshot.data() as RealtimeGameData;
    }
    
    return null;
  }

  /**
   * 연결 상태 확인
   */
  isConnected(): boolean {
    return this.unsubscribe !== null && this.roomId !== null;
  }

  /**
   * 현재 방 ID 가져오기
   */
  getCurrentRoomId(): string | null {
    return this.roomId;
  }
}

// 싱글톤 인스턴스
export const realtimeGameSync = new RealtimeGameSync();
